/**
 * User mypage order detail: zebra striping, customer estimate PDF, upper-limit scroll.
 */

import { bindOrderNotesNotifyConfirm } from '../share/order-notes-notify-confirm';

/**
 * @param {Element} row
 * @returns {boolean}
 */
function userOrderDetailRowIsHidden(row) {
    return row.closest('[hidden]') !== null;
}

/**
 * @param {HTMLElement} body
 * @returns {void}
 */
function userOrderDetailApplyStripingToBody(body) {
    body.querySelectorAll('.form-table-row').forEach((row) => {
        row.classList.remove('orders-edit-stripe--grey', 'orders-edit-stripe--white');
    });

    let stripeRowCount = 0;

    body.querySelectorAll('.form-table-row').forEach((row) => {
        if (userOrderDetailRowIsHidden(row)) {
            return;
        }
        const isGrey = stripeRowCount % 2 === 0;
        row.classList.add(isGrey ? 'orders-edit-stripe--grey' : 'orders-edit-stripe--white');
        stripeRowCount += 1;
    });
}

/**
 * @returns {void}
 */
function userOrderDetailApplyFormTableStriping() {
    const root = document.getElementById('user-mypage-order-detail-root');
    if (!root) {
        return;
    }

    root.querySelectorAll('.form-table-block__body').forEach((body) => {
        if (body instanceof HTMLElement) {
            userOrderDetailApplyStripingToBody(body);
        }
    });
}

/**
 * @param {string} value
 * @returns {boolean}
 */
function userOrderDetailCustomerCoefficientInputIsValid(value) {
    const trimmed = value.trim();
    if (trimmed === '') {
        return false;
    }
    return /^\d+(\.\d{1,2})?$/.test(trimmed);
}

/**
 * @returns {void}
 */
function userOrderDetailBindCustomerCoefficientField() {
    const root = document.getElementById('user-mypage-order-detail-root');
    if (!root) {
        return;
    }

    const input = root.querySelector('#user_order_customer_coefficient');
    if (!(input instanceof HTMLInputElement)) {
        return;
    }

    input.addEventListener('beforeinput', (e) => {
        if (!(e instanceof InputEvent) || e.data === null || e.data === '') {
            return;
        }
        const next =
            input.value.slice(0, input.selectionStart ?? 0) +
            e.data +
            input.value.slice(input.selectionEnd ?? 0);
        if (!/^\d*(\.\d{0,2})?$/.test(next)) {
            e.preventDefault();
        }
    });

    input.addEventListener('paste', (e) => {
        const text = e.clipboardData?.getData('text') ?? '';
        if (text !== '' && !/^\d*(\.\d{0,2})?$/.test(text.trim())) {
            e.preventDefault();
        }
    });

    input.addEventListener('input', () => {
        const cleaned = input.value.replace(/[^\d.]/g, '');
        const parts = cleaned.split('.');
        const normalized = parts.length <= 1 ? parts[0] ?? '' : `${parts[0]}.${parts.slice(1).join('')}`;
        if (normalized !== input.value) {
            input.value = normalized;
        }
    });
}

/**
 * 画面上部を覆う fixed/sticky バー（購入者 .loginNameVox / レスポンシブ #siteHeader）の下端オフセット。
 * 左サイドレール型の #siteHeader（高さほぼ viewport）は対象外。
 *
 * @returns {number} scrollY から差し引くピクセル（余白込み）
 */
function userOrderDetailTopOverlayOffsetPx() {
    const breathingRoomPx = 16;
    let offsetPx = breathingRoomPx;
    const overlayCandidates = [
        document.getElementById('siteHeader'),
        document.querySelector('.loginNameVox'),
    ];

    for (const overlayElement of overlayCandidates) {
        if (!(overlayElement instanceof HTMLElement)) {
            continue;
        }

        const computedStyle = window.getComputedStyle(overlayElement);
        if (computedStyle.position !== 'fixed' && computedStyle.position !== 'sticky') {
            continue;
        }

        const overlayRect = overlayElement.getBoundingClientRect();
        const isTopStripOverlay =
            overlayRect.top <= 1 &&
            overlayRect.bottom > 0 &&
            overlayRect.height < window.innerHeight * 0.5;
        if (!isTopStripOverlay) {
            continue;
        }

        offsetPx = Math.max(offsetPx, Math.ceil(overlayRect.bottom) + breathingRoomPx);
    }

    return offsetPx;
}

/**
 * 受注確認で「上限金について」未選択のとき、該当ブロックへスクロールする。
 * ブロックが DOM にある＝取付販売・受注確認かつ工事情報あり（サーバ条件で表示されたときのみ）。
 * 上部固定ヘッダーで隠れないよう {@see userOrderDetailTopOverlayOffsetPx} で位置をずらす。
 *
 * @returns {void}
 */
function userOrderDetailScrollToUnselectedUpperLimit() {
    const root = document.getElementById('user-mypage-order-detail-root');
    if (!root) {
        return;
    }

    const upperLimitBlock = root.querySelector('#installation-upper-limit');
    if (!(upperLimitBlock instanceof HTMLElement)) {
        return;
    }

    const selectedUpperLimit = upperLimitBlock.querySelector(
        'input[name="installation_upper_limit_type"]:checked',
    );
    if (selectedUpperLimit instanceof HTMLInputElement) {
        return;
    }

    const topOverlayOffsetPx = userOrderDetailTopOverlayOffsetPx();
    const targetScrollTop =
        upperLimitBlock.getBoundingClientRect().top + window.scrollY - topOverlayOffsetPx;
    window.scrollTo({ top: Math.max(0, targetScrollTop), behavior: 'auto' });
}

/**
 * 【AC0040】顧客用見積書: POST（order_id, coefficient, customer_pdf_*, optput_mode=1）。
 *
 * @param {HTMLFormElement} form
 * @param {string} name
 * @param {string} value
 * @returns {void}
 */
function userOrderDetailAppendHiddenFormField(form, name, value) {
    const field = document.createElement('input');
    field.type = 'hidden';
    field.name = name;
    field.value = value;
    form.appendChild(field);
}

/**
 * @returns {void}
 */
function userOrderDetailBindCustomerEstimatePdfDownload() {
    const root = document.getElementById('user-mypage-order-detail-root');
    if (!root) {
        return;
    }

    const trigger = root.querySelector('[data-user-customer-estimate-pdf="1"]');
    if (!(trigger instanceof HTMLElement)) {
        return;
    }

    const postUrl = trigger.getAttribute('data-customer-estimate-post-url') ?? '';
    const orderId = trigger.getAttribute('data-order-id') ?? '';
    const coefficientRequiredMessage =
        trigger.getAttribute('data-customer-estimate-coefficient-alert')?.trim() ?? '';
    const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? '';

    trigger.addEventListener('click', (e) => {
        e.preventDefault();
        if (postUrl === '' || postUrl === '#' || orderId === '' || csrf === '') {
            return;
        }

        const coefficientInput = root.querySelector('#user_order_customer_coefficient');
        const rawCoefficient = coefficientInput instanceof HTMLInputElement ? coefficientInput.value : '';
        if (!userOrderDetailCustomerCoefficientInputIsValid(rawCoefficient)) {
            const message = coefficientRequiredMessage.trim();
            if (message !== '' && typeof window.showAlert === 'function') {
                void window.showAlert(message);
            }
            return;
        }

        const zipInput = root.querySelector('#user_order_customer_pdf_zip');
        const addressInput = root.querySelector('#user_order_customer_pdf_address');
        const recipientNameInput = root.querySelector('#user_order_customer_pdf_recipient_name');
        const honorificChecked = root.querySelector(
            'input[name="user_order_customer_pdf_honorific"]:checked',
        );

        const form = document.createElement('form');
        form.method = 'post';
        form.action = postUrl;
        form.target = '_blank';
        form.style.display = 'none';

        userOrderDetailAppendHiddenFormField(form, '_token', csrf);
        userOrderDetailAppendHiddenFormField(form, 'order_id', orderId);
        userOrderDetailAppendHiddenFormField(form, 'coefficient', rawCoefficient.trim());
        userOrderDetailAppendHiddenFormField(
            form,
            'customer_pdf_zip',
            zipInput instanceof HTMLInputElement ? zipInput.value.trim() : '',
        );
        userOrderDetailAppendHiddenFormField(
            form,
            'customer_pdf_address',
            addressInput instanceof HTMLInputElement ? addressInput.value.trim() : '',
        );
        userOrderDetailAppendHiddenFormField(
            form,
            'customer_pdf_recipient_name',
            recipientNameInput instanceof HTMLInputElement ? recipientNameInput.value.trim() : '',
        );
        userOrderDetailAppendHiddenFormField(
            form,
            'customer_pdf_honorific',
            honorificChecked instanceof HTMLInputElement ? honorificChecked.value.trim() : 'sama',
        );
        userOrderDetailAppendHiddenFormField(form, 'optput_mode', '1');

        document.body.appendChild(form);
        form.submit();
        form.remove();
    });
}

document.addEventListener('DOMContentLoaded', () => {
    userOrderDetailApplyFormTableStriping();
    userOrderDetailBindCustomerCoefficientField();
    userOrderDetailBindCustomerEstimatePdfDownload();
    bindOrderNotesNotifyConfirm({
        formId: 'user_order_detail_form',
        notesFieldId: 'order_notes',
        notifyFieldId: 'notify_on_notes_change',
        modalId: 'userOrdersNotesNotifyModal',
        yesButtonId: 'userOrdersNotesNotifyYes',
        noButtonId: 'userOrdersNotesNotifyNo',
    });
    userOrderDetailScrollToUnselectedUpperLimit();
});
